import React from 'react';
import { 
  ShieldCheck, 
  AlertTriangle, 
  XCircle, 
  Clock, 
  CheckCircle,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const ComplianceStatusCard = ({ 
  complianceData = [],
  className = "",
  showTrend = true,
  showDetails = true 
}) => {
  const overallEntry = complianceData.find(item => 
    item?.key === 'overall-compliance' ||
    (typeof item?.title === 'string' && item.title.toLowerCase().includes('overall'))
  );

  // Calculate overall compliance percentage
  const calculateOverallCompliance = () => {
    if (!complianceData.length) return 0;

    if (overallEntry && (overallEntry.total || overallEntry.percentage)) {
      if (typeof overallEntry.percentage === 'number') {
        return overallEntry.percentage;
      }

      if (overallEntry.total) {
        const safeCompliant = Math.min(overallEntry.compliant ?? 0, overallEntry.total);
        return Math.round((safeCompliant / overallEntry.total) * 100);
      }
    }

    const totals = complianceData.reduce((acc, item) => {
      const total = item.total ?? 0;
      const compliant = Math.min(item.compliant ?? 0, total);
      return {
        total: acc.total + total,
        compliant: acc.compliant + compliant
      };
    }, { total: 0, compliant: 0 });

    if (totals.total === 0) {
      return 0;
    }

    return Math.round((totals.compliant / totals.total) * 100);
  };

  // Calculate category breakdown
  const calculateBreakdown = () => {
    if (!complianceData.length) return { compliant: 0, nearExpiry: 0, expired: 0 };
    
    const breakdownItems = overallEntry
      ? complianceData.filter(item => item !== overallEntry)
      : complianceData;

    const totals = breakdownItems.reduce((acc, item) => {
      const total = item.total ?? 0;
      const compliant = Math.min(item.compliant ?? 0, total);
      const expiring = typeof item.expiring === 'number' ? item.expiring : null;
      const expired = typeof item.expired === 'number' ? item.expired : null;

      acc.total += total;
      acc.compliant += compliant;

      if (expiring !== null) {
        acc.expiring += expiring;
        acc.hasExpiring = true;
      }

      if (expired !== null) {
        acc.expired += expired;
        acc.hasExpired = true;
      }

      return acc;
    }, { total: 0, compliant: 0, expiring: 0, expired: 0, hasExpiring: false, hasExpired: false });

    // If we excluded the overall entry and have no data left, fall back to overall entry numbers
    if (totals.total === 0 && overallEntry) {
      const total = overallEntry.total ?? 0;
      const compliant = Math.min(overallEntry.compliant ?? 0, total);
      const expiring = typeof overallEntry.expiring === 'number' ? overallEntry.expiring : 0;
      const expired = typeof overallEntry.expired === 'number' ? overallEntry.expired : Math.max(0, total - compliant - expiring);

      if (total === 0) {
        return { compliant: 0, nearExpiry: 0, expired: 0 };
      }

      const compliantPercent = Math.round((compliant / total) * 100);
      const nearExpiryPercent = Math.round((expiring / total) * 100);
      let expiredPercent = Math.round((expired / total) * 100);

      const adjustment = compliantPercent + nearExpiryPercent + expiredPercent - 100;
      if (adjustment > 0) {
        expiredPercent = Math.max(0, expiredPercent - adjustment);
      }

      return {
        compliant: compliantPercent,
        nearExpiry: nearExpiryPercent,
        expired: expiredPercent
      };
    }

    if (totals.total === 0) {
      return { compliant: 0, nearExpiry: 0, expired: 0 };
    }

    const inferredExpiringCount = totals.hasExpiring
      ? totals.expiring
      : Math.max(0, totals.total - totals.compliant - (totals.hasExpired ? totals.expired : 0));

    const inferredExpiredCount = totals.hasExpired
      ? totals.expired
      : Math.max(0, totals.total - totals.compliant - inferredExpiringCount);

    const compliantPercent = Math.round((totals.compliant / totals.total) * 100);
    let nearExpiryPercent = Math.round((inferredExpiringCount / totals.total) * 100);
    let expiredPercent = Math.round((inferredExpiredCount / totals.total) * 100);

    const totalPercent = compliantPercent + nearExpiryPercent + expiredPercent;
    if (totalPercent > 100) {
      const overflow = totalPercent - 100;
      if (expiredPercent >= overflow) {
        expiredPercent -= overflow;
      } else if (nearExpiryPercent >= overflow) {
        nearExpiryPercent = Math.max(0, nearExpiryPercent - overflow);
      }
    }

    return {
      compliant: Math.max(0, compliantPercent),
      nearExpiry: Math.max(0, nearExpiryPercent),
      expired: Math.max(0, expiredPercent)
    };
  };

  const overallCompliance = calculateOverallCompliance();
  const breakdown = calculateBreakdown();

  // Determine status based on compliance percentage
  const getComplianceStatus = (percentage) => {
    if (percentage >= 90) return 'excellent';
    if (percentage >= 75) return 'good';
    if (percentage >= 60) return 'warning';
    return 'critical';
  };

  const status = getComplianceStatus(overallCompliance);

  // Status configuration
  const statusConfig = {
    excellent: {
      color: 'success',
      bgColor: 'bg-success-50',
      textColor: 'text-success-700',
      borderColor: 'border-success-200',
      icon: CheckCircle,
      label: 'Excellent',
      description: 'All systems operating within compliance standards'
    },
    good: {
      color: 'primary',
      bgColor: 'bg-primary-50',
      textColor: 'text-primary-700',
      borderColor: 'border-primary-200',
      icon: ShieldCheck,
      label: 'Good',
      description: 'Minor compliance items need attention'
    },
    warning: {
      color: 'warning',
      bgColor: 'bg-warning-50',
      textColor: 'text-warning-700',
      borderColor: 'border-warning-200',
      icon: AlertTriangle,
      label: 'Warning',
      description: 'Several compliance issues require immediate action'
    },
    critical: {
      color: 'danger',
      bgColor: 'bg-danger-50',
      textColor: 'text-danger-700',
      borderColor: 'border-danger-200',
      icon: XCircle,
      label: 'Critical',
      description: 'Urgent compliance violations must be addressed'
    }
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  const trendChange = typeof overallEntry?.trendChange === 'number'
    ? overallEntry.trendChange
    : 0;
  const isPositiveTrend = trendChange > 0;

  return (
    <div className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200 ${className}`}>
      {/* Header */}
      <div className={`p-6 ${config.bgColor} ${config.borderColor} border-b`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg bg-white shadow-sm`}>
              <StatusIcon className={`h-6 w-6 text-${config.color}-600`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Compliance Status</h3>
              <p className={`text-sm ${config.textColor} font-medium`}>
                {config.label} - {overallCompliance}% Compliant
              </p>
            </div>
          </div>
          
          {showTrend && trendChange !== 0 && (
            <div className="flex items-center space-x-1">
              {isPositiveTrend ? (
                <TrendingUp className="h-4 w-4 text-success-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-danger-600" />
              )}
              <span className={`text-sm font-medium ${
                isPositiveTrend ? 'text-success-600' : 'text-danger-600'
              }`}>
                {Math.abs(trendChange)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Overall Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Overall Compliance</span>
            <span className="text-2xl font-bold text-slate-900">{overallCompliance}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div 
              className={`bg-${config.color}-500 h-3 rounded-full transition-all duration-500 ease-out`}
              style={{ width: `${overallCompliance}%` }}
            ></div>
          </div>
          <p className="text-xs text-slate-600 mt-2">{config.description}</p>
        </div>

        {/* Breakdown by Status */}
        {showDetails && (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Compliance Breakdown</h4>
            
            {/* Compliant */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-success-500 rounded-full"></div>
                <span className="text-sm text-slate-700">Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-slate-900">{breakdown.compliant}%</span>
                <div className="w-16 bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-success-500 h-2 rounded-full"
                    style={{ width: `${breakdown.compliant}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Near Expiry */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-warning-500 rounded-full"></div>
                <span className="text-sm text-slate-700">Near Expiry</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-slate-900">{breakdown.nearExpiry}%</span>
                <div className="w-16 bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-warning-500 h-2 rounded-full"
                    style={{ width: `${breakdown.nearExpiry}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Expired/Non-Compliant */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-danger-500 rounded-full"></div>
                <span className="text-sm text-slate-700">Non-Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-slate-900">{breakdown.expired}%</span>
                <div className="w-16 bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-danger-500 h-2 rounded-full"
                    style={{ width: `${breakdown.expired}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Items */}
        {showDetails && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Next Actions</h4>
            <div className="space-y-2">
              {status === 'excellent' && (
                <div className="flex items-center space-x-2 text-sm text-slate-600">
                  <Clock className="h-4 w-4" />
                  <span>Continue monitoring scheduled renewals</span>
                </div>
              )}
              {status === 'good' && (
                <div className="flex items-center space-x-2 text-sm text-slate-600">
                  <AlertTriangle className="h-4 w-4 text-warning-500" />
                  <span>Review {complianceData.length} compliance categories</span>
                </div>
              )}
              {(status === 'warning' || status === 'critical') && (
                <>
                  <div className="flex items-center space-x-2 text-sm text-danger-600">
                    <XCircle className="h-4 w-4" />
                    <span>Address {breakdown.expired}% non-compliant items</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-warning-600">
                    <Clock className="h-4 w-4" />
                    <span>Schedule renewals for {breakdown.nearExpiry}% expiring items</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceStatusCard;
